import { Injectable } from '@angular/core';
import { 
    Firestore, collection, collectionData, doc, 
    getDoc, getDocs, setDoc, updateDoc, deleteDoc, 
    query, where, onSnapshot, CollectionReference,
    orderBy, limit, startAt, endAt, docData, DocumentReference
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import type { 
    DocumentData, // OrderByDirection, WhereFilterOp, 
    Query, QueryConstraint, QuerySnapshot 
} from '@angular/fire/firestore';
import type { TQueryOptions } from '@shared/models';
import { getStorage, ref, uploadString } from '@angular/fire/storage';
// import { serverTimestamp } from '@angular/fire/firestore';
import { DateService } from './date.service';

@Injectable({
    'providedIn': 'root'
})
export class FirestoreService {
    constructor(private firestore: Firestore, private dateService: DateService) {
    }

    // Generate a unique ID similar to AngularFirestore.createId()
    public createUid(): string {
        const uid = doc(collection(this.firestore, '_')).id;
        return uid;
    }

    // public getObservable(collectionPath: string): any {
    //     const collectionRef = collection(this.firestore, collectionPath);
    //     const observable = collectionData(collectionRef, { idField: 'uid' });
    //     return observable;
    // }

    /**
     * Returns an Observable for a given Firestore path.
     * Automatically detects whether the path is a collection or document.
     * 
     * @param firestore - Firestore instance
     * @param path - The Firestore path (e.g., 'users' or 'users/123')
     * @param options - Optional configuration
     * @returns Observable that emits data from the specified path
     * 
     * @example
     * // Get a collection
     * getObservable(firestore, 'users').subscribe(users => console.log(users));
     * 
     * // Get a document
     * getObservable(firestore, 'users/abc123').subscribe(user => console.log(user));
     */
    // public getFirestoreObservable<T = any>(path: string, options?: 
    // {idField?: string;  // Name of the field to include document ID (default: 'id')}
    public getObservable<T = any>(path: string): Observable<T[] | T | undefined> {
        const { idField = 'uid' } = {};
        
        // Split path into segments
        const segments = path.split('/').filter(s => s.length > 0);
        
        // Determine if path is a collection (odd number of segments) or document (even)
        const isCollection = segments.length % 2 !== 0;
        
        if (isCollection) {
            // Handle collection path
            const collectionRef = collection(this.firestore, path) as CollectionReference<T>;
            return collectionData(collectionRef) as Observable<T[]>;
        } else {
            // Handle document path
            const docRef = doc(this.firestore, path) as DocumentReference<T>;
            return docData(docRef) as Observable<T | undefined>;
        }
    }

    public subscribeChangeListener(collectionPath: string, callback: (snapshot: DocumentData) => void) {
        const collectionRef = collection(this.firestore, collectionPath);
        // Set up snapshot listener
        const listener = onSnapshot(collectionRef, (snapshot) => {
            callback(snapshot);
        });
    }

    // Use add when I want the uid to be autogenerated for me.
    public add(collectionPath: string, obj: any): Promise<unknown> {
        obj.uid = this.createUid();
        return this.set(collectionPath, obj);
    }

    // Use set when I want a specific uid name, e.g., 'accountProfile' or zipcode 91351
    public async set(collectionPath: string, obj: any): Promise<unknown> {
        // The set will fail if this is in the object.
        delete obj.$$hashKey;
        obj.createdDate = this.dateService.now();   // serverTimestamp;

        const docRef = doc(this.firestore, collectionPath, obj.uid);
        await setDoc(docRef, obj);
        return obj;
    }

    // Use update when I want to update the object defined by the uid with just partial data.
    public async update(collectionPath: string, obj: any): Promise<unknown> {
        delete obj.$$hashKey;
        obj.modifiedDate = this.dateService.now();  // serverTimestamp;

        const docRef = doc(this.firestore, collectionPath, obj.uid);
        await updateDoc(docRef, obj);
        return obj;
    }

    // Return an array of all the items in this collection
    public async getAll(collectionPath: string): Promise<unknown> {
        const items: unknown[] = [];

        // If we are not passed a collectionPath, then return immediately.
        if (!collectionPath) {
            return items;
        }

        const collectionRef = collection(this.firestore, collectionPath);
        const snapshot = await getDocs(collectionRef);
        snapshot.forEach((doc) => {
            const item = doc.data();
            item['uid'] = doc.id;
            items.push(item);
        });
        return items;
    }

    public async get(documentPath: string): Promise<unknown> {
        try {
            const docRef = doc(this.firestore, documentPath);
            const snapshot = await getDoc(docRef);
            const item = (snapshot.exists()) ? snapshot.data() : null;
            if (item) {
                item['uid'] = snapshot.id;
            }
            return item;
        } catch (error) {
            throw new Error('Unable to get document: ' + error);
        }
    }

    public async getByUid(collectionPath: string, uid: string): Promise<unknown> {
        return await this.get(`${collectionPath}/${uid}`);
    }

    public async getByFieldValue(collectionPath: string, fieldName: string, fieldValue: any): Promise<unknown> {
        const collectionRef = collection(this.firestore, collectionPath);
        const q = query(collectionRef, where(fieldName, '==', fieldValue));
        const snapshot = await getDocs(q);

        const items: unknown[] = [];
        snapshot.forEach((doc) => {
            const item = doc.data();
            item['uid'] = doc.id;
            items.push(item);
        });
        return items;
    }
    // The incoming ref points to a node.  Save the incoming object with this key underneath this ref.
    // public save(collection: AngularFirestoreCollection, obj: any): Promise<{}> {
    //     // The save will fail if this is in the object.  If it's not in the object will the delete fail?
    //     delete obj.$$hashKey;
    //     const promise = new Promise(async (resolve, reject) => {
    //         // const connection: any = await this.internetConnected();
    //         // if (connection.isConnected) {
    //             collection.doc(obj.uid).update(obj);
    //             resolve(obj);
    //         // }
    //     });
    //     return promise;
    // }

    // The incoming ref points to a node.  Delete the node underneath this ref
    // that has this uid.
    // public removeChild(collection: AngularFirestoreCollection, uid: string): any {
    //     return this.delete(collection, uid);
    // }

    public async delete(collectionPath: string, uid: string): Promise<void> {
        const docRef = doc(this.firestore, collectionPath, uid);
        await deleteDoc(docRef);
        return;
    }

    public async removeAllChildren(collectionPath: string): Promise<boolean> {
        const items: any = await this.getAll(collectionPath);
        
        for (const item of items) {
            await this.delete(collectionPath, item.uid);
        }
        return true;
    }
    public async writeToFirebaseStorage(filename: string, data: string): Promise<void> {
        const storage = getStorage();
        const storageRef = ref(storage, filename);
        await uploadString(storageRef, data);
    }
    
    public async size(collectionPath: string): Promise<number> {
        const collectionRef = collection(this.firestore, collectionPath);
        const snapshot = await getDocs(collectionRef);
        return snapshot.size;
    }

    /**
     * Creates a Firestore query based on provided options
     * @param queryOptions Options for building the query
     * @returns Firestore Query object
     */
    public createQuery(queryOptions: TQueryOptions): Query<DocumentData> {
        const collectionRef = collection(this.firestore, queryOptions.collectionPath);
        
        const constraints: QueryConstraint[] = [];
        
        // Add orderBy if specified
        if (queryOptions.orderByField) {
            constraints.push(orderBy(
                queryOptions.orderByField, 
                queryOptions.orderByDirection || 'asc'
            ));
        }
        
        // Add startAt if specified (for range queries)
        if (queryOptions.startAt !== undefined) {
            constraints.push(startAt(queryOptions.startAt));
        }
        
        // Add endAt if specified (for range queries)
        if (queryOptions.endAt !== undefined) {
            constraints.push(endAt(queryOptions.endAt));
        }
        
        // Add limit if specified
        if (queryOptions.limitCount) {
            constraints.push(limit(queryOptions.limitCount));
        }
        
        // Add where filters if specified
        if (queryOptions.whereFilters && queryOptions.whereFilters.length > 0) {
            for (const filter of queryOptions.whereFilters) {
                constraints.push(where(filter.field, filter.operator, filter.value));
            }
        }
        
        // Create and return query
        return query(collectionRef, ...constraints);
    }
    /**
     * Executes a Firestore query and returns the raw snapshot
     * @param q The query to execute
     * @returns QuerySnapshot with the results
     */
    public async executeQuery(q: Query<DocumentData>): Promise<QuerySnapshot<DocumentData>> {
        return getDocs(q);
    }

    /**
     * Executes a query and returns the results as an array of objects
     * @param q The query to execute
     * @returns Array of documents with their data and IDs
     */
    public async executeQueryAndGetResults(q: Query<DocumentData>): Promise<unknown[]> {
        const snapshot = await this.executeQuery(q);
        
        const results: unknown[] = [];
        snapshot.forEach((doc) => {
            const data = doc.data();
            data['uid'] = doc.id;
            results.push(data);
        });
        
        return results;
    }

    /**
     * Convenience method to create and execute a query in one step
     * @param queryOptions Options for building the query
     * @returns Array of documents matching the query
     */
    public async queryCollection(queryOptions: TQueryOptions): Promise<unknown[]> {
        const q = this.createQuery(queryOptions);
        return this.executeQueryAndGetResults(q);
    }

    // public async internetConnected(): Promise<{}> {
    //     const collection = this.firestorePathsService.getInternetConnectedCol();
    //     // const doc = collection.doc();

    //     const promise = new Promise(async (resolve, reject) => {
    //         // await collection.once('value', (snap) => {
    //         const ref = collection.ref;
    //         const item = await this.get(ref);       // collection.once('value', (snap) => {
    //         const isConnected = (snap.val() === true);
    //         const msg = (isConnected ? 'Internet connected.' : 'No internet connection.  Please try again later.');
    //         const obj = {'isConnected': isConnected, 'msg': msg};
    //         resolve(obj);
    //     });
    //     return promise;
    // }
}
